import isNil from 'lodash/isNil';
import StorageUtils from '@spothero/utils/storage';
import OptimizelyUtils from '@spothero/utils/optimizely';
import Config from 'hooks/config';

/**
 * Optimizely feature flag keys
 * Defines the flags related to various experiments and features.
 */
export const FLAGS = {
    dashboardMTDOverride: 'control_panel_-_dashboard_mtd_override',
    maintenanceScreenEnabled: 'control_panel_-_maintenance_screen',
    trfLocations: 'control_panel_trf_locations',
    mtdMockDateEnabled: 'control_panel_-_mtd_mock_date_enabled',
} as const;

/** Prefix for feature flag override cookies. */
const EXPERIMENT_OVERRIDE_PREFIX = 'SH_EXPERIMENT_OVERRIDE';

/**
 * Gets the value of the experiment override cookie for the given feature name.
 * Returns null if the cookie does not exist.
 *
 * @param featureName - The name of the feature.
 * @returns The overridden value as boolean or null if no override exists.
 */
const getExperimentOverride = (featureName: string): boolean | null => {
    const cookieName = `${EXPERIMENT_OVERRIDE_PREFIX}_${featureName}`;

    return StorageUtils.get(cookieName, 'cookie');
};

/**
 * Checks if a feature is enabled for a user.
 * In non-production environments, this method first checks for an override via cookies.
 * Otherwise, it directly relies on Optimizely's `isFeatureEnabled` API.
 *
 * @param featureName - The name of the feature.
 * @returns Whether the feature is enabled (`true` / `false`) for the user.
 */
export const isFeatureEnabled = (featureName: string): boolean => {
    try {
        if (!Config.isProduction) {
            const overrideValue = getExperimentOverride(featureName);

            if (!isNil(overrideValue)) {
                return Boolean(overrideValue);
            }
        }

        return OptimizelyUtils.isFeatureEnabled(featureName);
    } catch (err) {
        console.error((err as Error).message);

        return false;
    }
};

/**
 * Parameters required to fetch a feature variable from Optimizely.
 */
interface FeatureVariableParams {
    featureName: string;
    variableName: string;
    type: 'Boolean' | 'Number' | 'String' | 'JSON';
}

/**
 * Gets the value of a feature variable for a given feature in Optimizely.
 *
 * @param params - An object containing feature name, variable name, and the expected type.
 * @returns The value of the given variable, which can be `boolean`, `number`, `string`, or `JSON`.
 */
export const getFeatureVariable = <T = unknown>(
    params: FeatureVariableParams
): T => {
    return OptimizelyUtils.getFeatureVariable(params);
};

/**
 * Location interface containing city and state properties.
 */
interface Location {
    city: string;
    state: string;
}

/**
 * Determines whether a given location is an applicable TRF location based on its city and state.
 * This relies on feature flags and variables defined in Optimizely.
 *
 * @param location - The location object with `city` and `state` properties.
 * @returns `true` if the location is applicable; otherwise, `false`.
 */
export const isTRFLocation = ({city, state}: Location): boolean => {
    if (!isFeatureEnabled(FLAGS.trfLocations)) {
        return false;
    }

    const applicableLocations = getFeatureVariable<{
        states: string[];
        cities: string[];
    }>({
        featureName: FLAGS.trfLocations,
        variableName: 'locations',
        type: 'JSON',
    });

    const applicableStates = applicableLocations.states.map((location) =>
        location.toLowerCase()
    );
    const applicableCities = applicableLocations.cities.map((location) =>
        location.toLowerCase()
    );
    const isApplicableState =
        state && applicableStates.includes(state.toLowerCase());
    const isApplicableCity =
        city && applicableCities.includes(city.toLowerCase());

    return isApplicableState || isApplicableCity;
};
